import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Dropdown,
  RetirementIncomeChart as RetirementIncomeChartComponent,
  Link,
  Slider,
} from '@trp-rps-rps-shared-web/rps-shared-web-components';
import * as tokens from '@trp-rps-rps-shared-web/rps-shared-web-styles';
import styled from 'styled-components';
import ChartHeader from './ChartHeader';
import { chartsActions, modalActions } from '../../redux/root-action';
import { formatAmountWithoutDecimals, getAge, keyDown } from '../../utils/helpers';
import { modals, accountTypeCodes, chartTypes, charts, showAsOptions, dollarTypes } from '../../utils/constants';
import { getIncomeGoal } from '../../selectors';
import { selectAge } from 'redux/charts/actions';

const fourColors = [
  '#33c2ff', //tokens.ColorChartingBrightBlue300,
  '#9aead6', //tokens.ColorChartingTeal100,
  '#9e7dcf', //tokens.ColorChartingTeal500,
  '#4e3473', //tokens.ColorChartingPurple500,
];

const sevenColors = [
  '#33c2ff', //tokens.ColorChartingBlue300,
  '#9aead6', //tokens.ColorChartingBrightBlue700,
  '#9e7dcf', //tokens.ColorChartingBrightBlue300,
  '#4e3473', //tokens.ColorChartingTeal700,
  '#27be99', //tokens.ColorChartingTeal300,
  tokens.ColorChartingPurple700,
  tokens.ColorChartingPurple300,
];

const elevenColors = [
  tokens.ColorChartingBlue500,
  tokens.ColorChartingBlue100,
  tokens.ColorChartingBrightBlue900,
  tokens.ColorChartingBrightBlue700,
  tokens.ColorChartingBrightBlue100,
  tokens.ColorChartingTeal900,
  tokens.ColorChartingTeal500,
  tokens.ColorChartingTeal100,
  tokens.ColorChartingPurple900,
  tokens.ColorChartingPurple500,
  tokens.ColorChartingPurple100,
];

const fifteenColors = [
  tokens.ColorBrandInteractiveBlue,
  tokens.ColorChartingBlue500,
  tokens.ColorChartingBlue100,
  tokens.ColorChartingBrightBlue900,
  tokens.ColorChartingBrightBlue700,
  tokens.ColorChartingBrightBlue700,
  tokens.ColorChartingBrightBlue100,
  tokens.ColorChartingTeal900,
  tokens.ColorChartingTeal700,
  tokens.ColorChartingTeal500,
  tokens.ColorChartingTeal100,
  tokens.ColorChartingPurple900,
  tokens.ColorChartingPurple700,
  tokens.ColorChartingPurple500,
  tokens.ColorChartingPurple100,
];

const mapColorPallete: Record<number, any> = {
  0: fourColors,
  1: fourColors,
  2: fourColors,
  3: fourColors,
  4: fourColors,
  5: sevenColors,
  6: sevenColors,
  7: sevenColors,
  8: elevenColors,
  9: elevenColors,
  10: elevenColors,
  11: elevenColors,
  12: fifteenColors,
  13: fifteenColors,
  14: fifteenColors,
  15: fifteenColors,
};

const StyledShowAsSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: inset 0 1px 0 0 ${tokens.ColorWhite200};
  background-color: rgba(0, 104, 148, 0.04);
  padding: 8px 64px 8px 64px;
  @media (min-width: 1024px) and (max-width: 1199px) {
    flex-wrap: wrap;
    padding: 8px 48px;
    position: absolute;
    bottom: 0;
  }
  @media (max-width: 768px) {
    display: none;
    padding: 8px 16px;
  }

  @media only screen and (orientation: landscape) and (min-device-width: 768px) and (max-device-width: 1024px) {
    display: none;
  }
`;

const StyledShowAsDropdown = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLinkContainer = styled.div`
  @media (max-width: 1024px) {
    margin-bottom: 24px;
  }
`;

const StyledChartContent = styled.div`
  padding: 0px 64px 0px 64px;
  @media (max-width: 1024px) {
    padding: 0px 48px 0px 48px;
  }
  @media (max-width: 768px) {
    padding: 0px;
  }
  > div:nth-of-type(2) {
    @media(min-width: 1025px) {
      display: block;
    }
  }
`;

const StyledSliderContainer = styled.div`
  // padding: 0 16px 0 54px;
  padding-top: 20px;
  margin: -16px 0 0 0;
  > div {
    margin: 0;
  }
  @media (max-width: 1024px) {
    margin: -10px -5px 0 -6px;
  }
`;

const TotalIncomeRow = styled.div`
  display: none;
  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    padding: 16px 0;
  }
`;

const TotalIncomeLabel = styled.div`
  flex: 50%;
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  line-height: 22px;
  text-align: left;
  color: ${tokens.ColorGrey900};
`;

const TotalIncomeValue = styled.div`
  flex: 50%;
  font-family: Nimbus Sans Bold;
  font-size: 16px;
  line-height: 22px;
  text-align: right;
  color: ${tokens.ColorGrey900};
`;

const ShowDetailsContainer = styled.div`
  margin: 24px 78px 8px 122px;
  position: relative;
  @media only screen and (max-device-width: 1024px) {
    display: block;
    margin: 24px 8px 8px;
  }
`;

const ShowDetailsHeader = styled.div`
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  line-height: 22px;
  text-align: left;
  margin-bottom: 8px;
  color: ${tokens.ColorBrandInteractiveBlue};
`;

const CollapseIcon = styled.img`
  margin-left: 8px;
  width: 8px;
  height: 8px;
  cursor: pointer;
`;

const GearIcon = styled.img`
  position: relative;
  top: -2px;
  margin-right: 8px;
`;

const ShowDetailsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid ${tokens.ColorWhite200};
  @media only screen and (min-device-width: 1024px) {
    border-bottom: none;
    padding: 4px 0;
  }
`;

const ShowDetailsRowWithColor = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid ${tokens.ColorWhite200};
  @media only screen and (min-device-width: 1024px) {
    border-bottom: none;
    padding: 4px 0 4px 36px;
  }
`;

const ShowDetailsLegend = styled.div`
  width: 24px;
`;

const ShowDetailsLegendDots = styled.div`
  height: 4px;
  width: 24px;
  overflow: hidden;
  border-bottom: 4px dotted #006894;

`;

const ShowDetailsLegendColor = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 2px;
  margin-rightt: 12px;
  background-color: ${props => props.color};
`;

const ShowDetailsLabel = styled.div`
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  line-height: 22px;
  text-align: left;
  color: ${tokens.ColorGrey900};
  padding-left: 8px;
  padding-right: 8px;
  cursor: pointer;
  @media only screen and (min-device-width: 1024px) {
    padding-left: 0;
  }
`;

const ShowDetailsValue = styled.div`
  flex: 30%;
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  line-height: 22px;
  text-align: right;
  color: ${tokens.ColorGrey900};
`;

const ShowDetailsLink = styled.div`
  margin-bottom: 24px;
`;

const ShowDetailsFootnote = styled.div`
  font-family: Nimbus Sans Regular;
  font-size: 12px;
  line-height: 18px;
  color: ${tokens.ColorGrey600};
  padding-bottom: 8px;
`;

const BoldText = styled.span`
  font-family: Nimbus Sans Bold;
`;

const PopoverContainer = styled.div`
  position: absolute;
  z-index: 1;
  transform: translateY(-140%);
  width: 318px;
  @media only screen and (min-device-width: 1024px) {
    margin-left: 34px;
    transform: translateY(-130%);
  }

  &:after, &:before {
    top: 100%;
    left: 18%;
    border: solid transparent;
    content: "";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }

  &:after {
    border-color: rgba(255, 255, 255, 0);
    border-top-color: ${tokens.ColorWhite000};;
    border-width: 8px;
    margin-left: -8px;
  }
  &:before {
    border-color: rgba(255, 255, 255, 0);
    border-top-color: ${tokens.ColorWhite000};
    border-width: 14px;
    margin-left: -14px;
  }
`;

const PopoverContent = styled.div`
  box-shadow: 0 16px 16px 0 rgba(45, 57, 64, 0.06), 0 0 8px 0 rgba(45, 57, 64, 0.06);
  background-color:  ${tokens.ColorWhite000};
  padding: 16px;
`;

const PopoverIncomeContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  div {
    flex: 1;
    font-size: 14px;
    line-height: 18px;
    padding-top: 8px;
    color:  ${tokens.ColorGrey700};
    :last-of-type {
      text-align: right;
    }
  }
`;

const HoverLink = styled.a`
  border-bottom: 1px dashed ${tokens.ColorBrandInteractiveBlue};
  color: ${tokens.ColorGrey900};
`;

class RetirementIncomeChart extends Component<any> {
  props: any;
  state: any;
  constructor(props: any) {
    super(props);
    this.state = {
      showDetails: true,
      showFutureIncome: true,
      showPopover: false,
      datapoint: 0,
    };
  }
  showEditSettingsModal = () => {
    const { showModal } = this.props;
    showModal(modals.EDIT_CHART_DISPLAY_SETTINGS);
  };

  showChartEducationModal = () => {
    const { showModal } = this.props;
    showModal(modals.CHART_EDUCATION);
  };

  getSimulationYearlyData = () => {
    const {
      selectedChartType,
      selectedDollarType,
      simulationCurrentWant,
      simulationCurrentHave,
      spouseRetirementStartAge,
      socialSecurityStartAge,
      spouseSocialSecurityStartAge,
      socialSecurityAmount,
      spouseSocialSecurityAmount,
      pensionStartAge,
      spousePensionStartAge,
      selectedAge,
      events,
    } = this.props;
    const simulationData = selectedChartType === chartTypes.WANT ? simulationCurrentWant : simulationCurrentHave;
    const {
      participantAge,
      simulationType,
      adjustedNetSavingsWithdrawal,
      adjustedSocialSecurity,
      adjustedPension,
      householdRequiredRetirementIncome,
      householdNetSalary,
      runoutAge,
      desiredRetirementAge,
      adjustedFutureNetSavingsWithdrawal,
      adjustedFutureSocialSecurity,
      adjustedFuturePension,
      householdNetSalaryInFutureDollars,
      householdRequiredRetirementIncomeInFutureDollars,
      householdTotalIncomeEvent,
      householdPensionAmount,
      householdIncomeEvent,
      retiredHouseholdEarnedIncome,
      retiredHouseholdEarnedIncomeInFutureDollars,
      householdIncomeEventInFutureDollars,
      householdTotalIncomeEventInFutureDollars,
    } = simulationData;
    const chartStartAge = desiredRetirementAge > participantAge[0] ? desiredRetirementAge - participantAge[0] : 0;

    let householdSavings = [],
      socialSecurity = [],
      pension = [],
      incomeGoal = [],
      spouseNetIncome = [],
      incomeEvents = [],
      retirementJob = [],
      spouseRetirementJob = [];

    if (selectedDollarType.value === dollarTypes.FUTURE_DOLLARS) {
      householdSavings = adjustedFutureNetSavingsWithdrawal?.slice(chartStartAge);
      socialSecurity = adjustedFutureSocialSecurity?.slice(chartStartAge);
      pension = adjustedFuturePension?.slice(chartStartAge);
      incomeGoal = householdRequiredRetirementIncomeInFutureDollars?.slice(chartStartAge);
      retirementJob = retiredHouseholdEarnedIncomeInFutureDollars[0]?.value.slice(chartStartAge);
      spouseRetirementJob = retiredHouseholdEarnedIncomeInFutureDollars[1]?.value.slice(chartStartAge);
      spouseNetIncome = householdNetSalaryInFutureDollars?.slice(chartStartAge);
      incomeEvents = householdTotalIncomeEventInFutureDollars?.slice(chartStartAge);
    } else {
      householdSavings = adjustedNetSavingsWithdrawal?.slice(chartStartAge);
      socialSecurity = adjustedSocialSecurity?.slice(chartStartAge);
      pension = householdPensionAmount?.slice(chartStartAge);
      incomeGoal = householdRequiredRetirementIncome?.slice(chartStartAge);
      retirementJob = retiredHouseholdEarnedIncome[0]?.value.slice(chartStartAge);
      spouseRetirementJob = retiredHouseholdEarnedIncome[1]?.value.slice(chartStartAge);
      spouseNetIncome = householdNetSalary?.slice(chartStartAge);
      incomeEvents = householdTotalIncomeEvent?.slice(chartStartAge);
    }

    const series = [];

    const allEqual = (arr: any) => arr.every((v: any) => v === 0);
    
    if (householdSavings && householdSavings.length && !allEqual(householdSavings)) {
      series.push({
        name: 'Household Savings*',
        type: 'column',
        data: householdSavings.map((amount: number) => Math.trunc(amount)),
      });
    }

    if (desiredRetirementAge < spouseRetirementStartAge && spouseNetIncome && spouseNetIncome.length && !allEqual(spouseNetIncome)) {
      series.push({
        name: 'Spouse Net Income',
        type: 'column',
        data: spouseNetIncome.map((amount: number) => Math.trunc(amount)),
      });
    }

    if (socialSecurity && socialSecurity.length && !allEqual(socialSecurity)) {
      series.push({
        name: 'Social Security*',
        type: 'column',
        data: socialSecurity.map((amount: number) => Math.trunc(amount)),
      });
    }

    if (incomeEvents && incomeEvents.length && !allEqual(incomeEvents)) {
      series.push({
        name: events && events.length === 1 ? events[0].name : 'Income Events',
        type: 'column',
        data: incomeEvents.map((amount: number) => Math.trunc(amount)),
      });
    }

    if (pension && pension.length && !allEqual(pension)) {
      series.push({
        name: 'Pension*',
        type: 'column',
        data: pension.map((amount: number) => Math.trunc(amount)),
      });
    }

    if (retirementJob && retirementJob.length && !allEqual(retirementJob)) {
      series.push({
        name: 'Retirement Job',
        type: 'column',
        data: retirementJob.map((amount: number) => Math.trunc(amount)),
      });
    }

    if (spouseRetirementJob && spouseRetirementJob.length && !allEqual(spouseRetirementJob)) {
      series.push({
        name: 'Spouse Retirement Job',
        type: 'column',
        data: spouseRetirementJob.map((amount: number) => Math.trunc(amount)),
      });
    }

    if (incomeGoal && incomeGoal.length) {
      series.push({
        type: 'line',
        name: 'Retirement Income Goal',
        pointStart: 0,
        data: incomeGoal.map((amount: number) => Math.trunc(amount)),
      });
    }

    

    return {
      participantAge: participantAge.slice(chartStartAge),
      runOutAge: runoutAge === 0 || participantAge.length === 0 ? null : runoutAge + participantAge[0],
      simulationType: simulationType,
      retirementAge: participantAge[chartStartAge],
      series,
      spouseRetirementStartAge,
      socialSecurityStartAge,
      spouseSocialSecurityStartAge,
      socialSecurityAmount,
      spouseSocialSecurityAmount,
      pensionStartAge,
      spousePensionStartAge,
      hoveredAge: selectedAge,
    };
  };

  getSimulationMonthlyData = () => {
    const monthlyData: any = [],
      yearlyData = this.getSimulationYearlyData() as any;
    yearlyData.series.forEach((item: any) => {
      monthlyData.push({ ...item, data: item.data.map((income: number) => Math.trunc(income / 12)) });
    });
    return { ...yearlyData, series: monthlyData };
  };

  expandShowDetails = () => {
    const { showDetails } = this.state;
    this.setState({
      showDetails: showDetails ? false : true,
    });
  };

  toggleFutureIncome = () => {
    const { showFutureIncome } = this.state;
    this.setState({
      showFutureIncome: showFutureIncome ? false : true,
    });
  };

  getTotalIncomeAtAge = (data: any, age: number) => {
    const selectedAge = age ? age : data.participantAge[0];
    return data.series.reduce((accum: number, current: any) => {
      if (current.name === 'Retirement Income Goal') {
        return accum;
      }
      return accum + current.data[selectedAge - data.participantAge[0]];
    }, 0);
  };

  getIncomeGapAtAge = (data: any, selectedAge: number) => {
    return (
      this.getTotalIncomeAtAge(data, selectedAge) -
      data.series.filter((series: any) => series.name === 'Retirement Income Goal')[0].data[
      selectedAge ? selectedAge - data.participantAge[0] : 0
      ]
    );
  };

  showIncomePopover = (isShowing: boolean, data: number) => {
    this.setState({ showPopover: isShowing ? false : true });
    this.setState({ datapoint: data });
  };

  render() {
    const {
      retirementShowAs,
      toggleRetirementShowAs,
      selectedChartType,
      selectAge,
      selectedAge,
      simulationCurrentWant,
      //simulationCurrentWant: { householdRetirementIncomeGoal },
      simulationCurrentHave,
      incomeGoal,
      birthDate,
      selectedDollarType
    } = this.props;
    const { showDetails, showFutureIncome, showPopover, datapoint } = this.state;
    const mq = window.matchMedia('(max-width: 767px)');

    let today = new Date();
    let dob = new Date(birthDate);
    let currentAge = today.getFullYear() - dob.getFullYear();

    if (
      (selectedChartType === chartTypes.WANT && !simulationCurrentWant.simulationType) ||
      (selectedChartType === chartTypes.HAVE && !simulationCurrentHave.simulationType)
    ) {
      return null;
    }

    const setHouseholdRetirementIncomeGoal = selectedChartType === 'want' ? simulationCurrentWant.householdRetirementIncomeGoal : simulationCurrentHave.householdRetirementIncomeGoal;
    const simulationData = selectedChartType === chartTypes.WANT ? simulationCurrentWant : simulationCurrentHave;
    const {
      householdIncomeEventInFutureDollars,
      householdIncomeEvent,
      desiredRetirementAge
    } = simulationData;
    const data =
      retirementShowAs.value === 'monthlyIncome' ? this.getSimulationMonthlyData() : this.getSimulationYearlyData();
    const colorPaletteForMobile = mapColorPallete[data.series.length - 1];
    const monthOrYear = retirementShowAs.value === 'monthlyIncome' ? '/mo.' : '/yr.';
    const mobile = window.matchMedia('(max-width: 1023px)');
    let canSpendTotal = data.series.reduce((accum: any, currVal: any) => {
      return accum + currVal
    }, 0);

    let ageValue = 0;
    
    let numberOfBars = data.participantAge && data.participantAge[data.participantAge.length - 1] - data.participantAge[0] + 1;
    let barWidth = Math.floor(535/numberOfBars);
    let paddingOffset = barWidth/2;
    let sliderPaddingLeft = 70 + paddingOffset - 14;
    let sliderPaddingRight = 14 + paddingOffset - 12;

    return (
      <React.Fragment>
        <ChartHeader
          id={charts.RETIREMENT_INCOME}
          onTrack={this.getSimulationYearlyData().runOutAge ? false : true}
          spendAmount={formatAmountWithoutDecimals(Math.trunc(incomeGoal))}
          householdRetirementIncomeGoal={formatAmountWithoutDecimals(Math.trunc(setHouseholdRetirementIncomeGoal))}
          runOutAge={this.getSimulationYearlyData().runOutAge}
        />
        <StyledChartContent>
          <RetirementIncomeChartComponent data={data} />
               
          <StyledSliderContainer style={{paddingLeft: sliderPaddingLeft+'px', paddingRight: sliderPaddingRight + 'px'}}>
            <Slider
              value={selectedAge ? selectedAge : data.participantAge[0]}
              min={data.participantAge[0]}
              max={data.participantAge[data.participantAge.length - 1]}
              onChange={(age: number) => selectAge(age)}
              ariaLabel="Age Slider Control"
            />
          </StyledSliderContainer>    
        </StyledChartContent>        
        <ShowDetailsContainer>
          <TotalIncomeRow>
            <TotalIncomeLabel>
              Total Income at <BoldText>Age {selectedAge ? selectedAge : data.participantAge[0]}</BoldText>
            </TotalIncomeLabel>
            <TotalIncomeValue>
              {formatAmountWithoutDecimals(this.getTotalIncomeAtAge(data, selectedAge))}
              {monthOrYear}
            </TotalIncomeValue>
          </TotalIncomeRow>
          {mobile.matches && (
            <>
              <ShowDetailsHeader
                onClick={this.expandShowDetails}
                tabIndex={0}
                onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.expandShowDetails)}
              >
                {showDetails ? 'Hide ' : 'Show '} Details
                <CollapseIcon
                  src={
                    showDetails
                      ? `data:image/svg+xml;base64,${tokens.AssetIconDropdownUpArrowBlue}`
                      : `data:image/svg+xml;base64,${tokens.AssetIconDropdownDownArrowBlue}`
                  }
                />
              </ShowDetailsHeader>
            </>
          )}
          {showDetails && (
            <>
              <ShowDetailsRow>
                {mobile.matches && (<ShowDetailsLegend>
                  <ShowDetailsLegendDots />
                </ShowDetailsLegend>)}
                <ShowDetailsLabel style={{ fontFamily: 'Nimbus Sans ' + (mobile.matches ? 'Regular' : 'Bold') }}>
                  {(mobile.matches ? '' : 'Age ' + (selectedAge ? selectedAge : data.participantAge[0]) + ' ') + data.series[data.series.length - 1].name}
                </ShowDetailsLabel>
                {!mobile.matches && (<ShowDetailsLegend>
                  <ShowDetailsLegendDots />
                </ShowDetailsLegend>)}
                <ShowDetailsValue>
                <BoldText>
                  {formatAmountWithoutDecimals(
                    data.series[data.series.length - 1].data[selectedAge ? selectedAge - data.participantAge[0] : 0],
                  )}
                  {monthOrYear}
                </BoldText>
                </ShowDetailsValue>
              </ShowDetailsRow>
              {!mobile.matches && (<ShowDetailsRow>
                <ShowDetailsLabel
                  onClick={this.toggleFutureIncome}
                  tabIndex={0}
                  onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.toggleFutureIncome)}>
                  <BoldText>What You Can Spend</BoldText>
                  </ShowDetailsLabel>
                  <CollapseIcon
                  onClick={this.toggleFutureIncome}
                  alt="What you can spend toggle icon"
                  src={
                    showFutureIncome
                      ? `data:image/svg+xml;base64,${tokens.AssetIconDropdownUpArrowBlue}`
                      : `data:image/svg+xml;base64,${tokens.AssetIconDropdownDownArrowBlue}`
                  }
                  />
                  <ShowDetailsValue>
                  <BoldText>
                    {formatAmountWithoutDecimals(Math.abs(this.getTotalIncomeAtAge(data, selectedAge)))}
                  </BoldText>
                </ShowDetailsValue>
              </ShowDetailsRow>)}
              {showFutureIncome && (
                data.series.map((series: any, i: number) => {
                  if (series.name === 'Retirement Income Goal') {
                    return null;
                  }
                  
                  ageValue = selectedAge ? selectedAge - currentAge : desiredRetirementAge - currentAge;
                  return (
                    <>
                      <ShowDetailsRowWithColor key={i}>
                        <ShowDetailsLegend>
                          <ShowDetailsLegendColor color={colorPaletteForMobile[i]} />
                        </ShowDetailsLegend>                 
                        <ShowDetailsLabel>
                          {series.name === 'Income Events' ? householdIncomeEventInFutureDollars.length === 1 ? householdIncomeEventInFutureDollars[0].name : (
                            mq.matches ? (
                              <HoverLink onClick={() => this.setState({ showPopover: showPopover ? false : true })}>{series.name}</HoverLink>
                            ) : (
                              <HoverLink onMouseEnter={() => this.setState({ showPopover: true })} onMouseLeave={() => this.setState({ showPopover: false })}>{series.name}</HoverLink>
                            )
                          ) : series.name
                          }
                        </ShowDetailsLabel>
                        <ShowDetailsValue>
                          {formatAmountWithoutDecimals(series.data[selectedAge ? selectedAge - data.participantAge[0] : 0])}
                          {monthOrYear}
                        </ShowDetailsValue>
                      </ShowDetailsRowWithColor>
                      {series.name === 'Income Events' && showPopover && (
                        <PopoverContainer>
                          <PopoverContent>
                            <ShowDetailsRowWithColor style={{ paddingLeft: 0, paddingBottom: 0, border: mq.matches ? 'none' : '' }} key={i}>
                              <ShowDetailsLegend>
                                <ShowDetailsLegendColor color={colorPaletteForMobile[i]} />
                              </ShowDetailsLegend>
                              <ShowDetailsLabel><BoldText>Income Events</BoldText></ShowDetailsLabel>
                              <ShowDetailsValue>
                                <BoldText>{formatAmountWithoutDecimals(series.data[selectedAge ? selectedAge - data.participantAge[0] : 0])}
                                  {monthOrYear}</BoldText>
                              </ShowDetailsValue>
                            </ShowDetailsRowWithColor>
                            
                            {selectedDollarType.value === dollarTypes.FUTURE_DOLLARS ?
                              householdIncomeEventInFutureDollars?.map((event: any, index: number) => {
                                return (
                                  <PopoverIncomeContainer>
                                    <div>{event?.name}</div>
                                    <div>{formatAmountWithoutDecimals(event?.value[ageValue])}</div>
                                  </PopoverIncomeContainer>
                                );
                              })
                              :
                              householdIncomeEvent?.map((event: any, index: number) => {
                                return (
                                  <PopoverIncomeContainer>
                                    <div>{event?.name}</div>
                                    <div>{formatAmountWithoutDecimals(event?.value[ageValue])}</div>
                                  </PopoverIncomeContainer>
                                );
                              })
                            }
                          </PopoverContent>
                        </PopoverContainer>
                      )}
                    </>
                  );
                })
              )}
              <ShowDetailsRow style={{ margin: !mobile.matches ? '32px 0 24px' : '0 0 24px' }}>
                <ShowDetailsLabel>
                  <BoldText>Income Gap</BoldText>
                </ShowDetailsLabel>
                <ShowDetailsValue
                  style={{
                    color:
                      this.getIncomeGapAtAge(data, selectedAge) >= 0
                        ? tokens.ColorFunctionalGreen500
                        : tokens.ColorFunctionalRed500,
                  }}
                >
                  <BoldText>
                    {formatAmountWithoutDecimals(Math.abs(this.getIncomeGapAtAge(data, selectedAge)))}
                  </BoldText>
                </ShowDetailsValue>
              </ShowDetailsRow>
              {mobile.matches && (
                <>
                  <ShowDetailsLink>
                    <Link
                      linkLabel={'Why does my goal change over time?'}
                      onClick={this.showChartEducationModal}
                      tabIndex={0}
                      onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.showChartEducationModal)}
                      color={tokens.ColorBrandInteractiveBlue}
                      hoverColor={tokens.ColorBrandInteractiveBlueHover}
                      id="goal-change-popup-link"
                    />
                  </ShowDetailsLink>
                  <ShowDetailsLink>
                    <GearIcon src={`data:image/svg+xml;base64,${tokens.AssetIconGear}`} />
                    <Link
                      linkLabel={'Edit Chart Display Settings'}
                      onClick={this.showEditSettingsModal}
                      tabIndex={0}
                      onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.showEditSettingsModal)}
                      color={tokens.ColorBrandInteractiveBlue}
                      hoverColor={tokens.ColorBrandInteractiveBlueHover}
                    />
                  </ShowDetailsLink>
                </>
              )}
              <ShowDetailsFootnote>
                *Higher withdrawal amounts may be necessary due to withholding requirements or the need to pay taxes.
                Estimated taxes have been taken out of Social Security and any applicable pension amounts.
              </ShowDetailsFootnote>
            </>
          )}
        </ShowDetailsContainer>

        <StyledShowAsSection>
          <StyledShowAsDropdown>
            <span>Show as: </span>
            <Dropdown
              label="Yearly Income Label"
              onChange={(option: any) => toggleRetirementShowAs(option)}
              selectedOption={retirementShowAs || {}}
              name="incomeToggle"
              arrowStyle="blue"
              errorMessage={''}
              textColor={tokens.ColorBrandInteractiveBlue}
              textDropdown={true}
              options={showAsOptions}
              id="yearly-or-monthly-dropdown"
            />
          </StyledShowAsDropdown>
          <StyledLinkContainer>
            <Link
              linkLabel={'Why does my goal change over time?'}
              onClick={this.showChartEducationModal}
              tabIndex={0}
              onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.showChartEducationModal)}
              color={tokens.ColorBrandInteractiveBlue}
              hoverColor={tokens.ColorBrandInteractiveBlueHover}
              id="goal-change-popup-link"
            />
          </StyledLinkContainer>
        </StyledShowAsSection>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: any) => {
  const {
    charts: {
      selectedChart,
      selectedAge,
      selectedChartType,
      selectedDollarType,
      retirementShowAs,
      simulationCurrentWant,
      simulationCurrentHave,
    },
    riScenario: {
      spouseProfile,
      participantProfile,
      spouseRetirementStartAge,
      socialSecurityStartAge,
      spouseSocialSecurityStartAge,
      socialSecurityAmount,
      spouseSocialSecurityAmount,
      events,
    },
  } = state;
  const pensions = events && events.filter((event: any) => event.categoryName === accountTypeCodes.PENSION);
  let pensionStartAge: any = null,
    spousePensionStartAge: any = null;
  if (pensions) {
    pensions.forEach((pension: any) => {
      if (pension.bpId === participantProfile.bpId) {
        if (pensionStartAge) {
          pensionStartAge = pensionStartAge > pension.startNumber ? pension.startNumber : pensionStartAge;
        } else {
          pensionStartAge = pension.startNumber;
        }
      } else if (spouseProfile && pension.bpId === spouseProfile.bpId) {
        if (spousePensionStartAge) {
          spousePensionStartAge =
            spousePensionStartAge > pension.startNumber ? pension.startNumber : spousePensionStartAge;
        } else {
          spousePensionStartAge = pension.startNumber;
        }
      }
    });
  }

  return {
    selectedChart,
    selectedChartType,
    selectedDollarType,
    retirementShowAs,
    simulationCurrentWant,
    simulationCurrentHave,
    spouseRetirementStartAge: spouseProfile
      ? spouseRetirementStartAge + getAge(participantProfile.birthDate) - getAge(spouseProfile.birthDate)
      : null,
    socialSecurityStartAge,
    socialSecurityAmount,
    spouseSocialSecurityStartAge: spouseProfile
      ? spouseSocialSecurityStartAge + getAge(participantProfile.birthDate) - getAge(spouseProfile.birthDate)
      : null,
    spouseSocialSecurityAmount: spouseProfile ? spouseSocialSecurityAmount : null,
    pensionStartAge,
    spousePensionStartAge:
      spouseProfile && spousePensionStartAge
        ? spousePensionStartAge + getAge(participantProfile.birthDate) - getAge(spouseProfile.birthDate)
        : null,
    selectedAge,
    incomeGoal: getIncomeGoal(state),
    events,
    birthDate: participantProfile && participantProfile.birthDate
  };
};

export default connect(mapStateToProps, {
  toggleRetirementShowAs: chartsActions.toggleRetirementShowAs,
  showModal: modalActions.showModal,
  selectAge: chartsActions.selectAge,
})(RetirementIncomeChart);
